<template>
  <div class="table-component p-2">
    <artisio-table selectable ref="invoiceTable" :pagination="tableData.pagination" :sort="tableData.ordering"
                   :items="items" :fields="tableFields" :checkboxes="true" :extra-data="extraData"
                   :search="tableData.search" :enable-simple-search="false" show-toggle-columns requires-min-height
                   :index="false" :busy="loading" enable-frontend-export export-vuex-module-name="vendors"
                   @paramsChanged="getTableData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns" filter-toggle-string="vendors-table">
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="vendors-table">
          <vendors-search/>
        </search-collapse>
      </template>

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown class="btn-table-actions" :text="$t('Actions')" variant="primary"
                    v-permission="'change_auction'">
          <b-dropdown-item v-permission="'change_auction'" :disabled="selectedItems.size === 0"
                           @click="onBatchSendProposalOfUnsoldLots(selectedItems)">
            {{ $t('Send Proposal of Unsold Lots') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_auction'" :disabled="selectedItems.size === 0"
                           @click="onBatchSendAuctionConfirmationNotification(selectedItems)">
            {{ $t('Send Auction Confirmation') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_auction'" :disabled="selectedItems.size === 0"
                           @click="onBatchSendAuctionResultsNotification(selectedItems)">
            {{ $t('Send Auction Results') }}
          </b-dropdown-item>
          <b-dropdown-item v-permission="'change_settlement'" :disabled="selectedItems.size === 0"
                           @click="onBatchGenerateSettlementsClick(selectedItems)">
            {{ $t('Generate Settlements') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template slot="cell(no)" slot-scope="{item}">
        <template v-if="item.customer">
          <span>{{ item.customer.customer_no }}</span>
        </template>
      </template>
      <template slot="cell(full_name)" slot-scope="{item}">
        <template v-if="item.customer">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: item.customer.uuid }}">
            {{ item.customer.display_name }}
          </router-link>
          <span v-else>{{ item.customer.display_name }}</span>
        </template>
      </template>
      <template slot="cell(company_name)" slot-scope="{item}">
        {{ item.customer.company_name }}
      </template>
      <template slot="cell(email)" slot-scope="{item}">
        <template v-if="item.customer.email">
          <span class="badge border mr-1">
            <a :href="`mailto:` + item.customer.email"> {{ item.customer.email }}</a>
          </span>
        </template>
      </template>
      <template slot="cell(client_tier)" slot-scope="{item}">
        <client-tier-display-widget :customer="item.customer" />
      </template>
      <template slot="cell(address)" slot-scope="{item}">
        <div v-if="item.display_billing_address"
             v-html="$options.filters.splitAddressForMultiline(item.display_billing_address)">
        </div>
      </template>
      <template slot="cell(financial_items)" slot-scope="{item}">
        <template v-if="item.financial_items">
          <a v-if="item.financial_items > 0" class="mr-3" href="javascript:void(0)"
             @click="onTotalItemsClick(item)">
            {{ item.financial_items }}
          </a>
          <span v-else>0</span>
        </template>
      </template>

      <template slot="cell(total_lots_sold)" slot-scope="{item}">
        <a v-if="item.total_lots_sold > 0" class="mr-3" href="javascript:void(0)"
           @click="onTotalItemsClick(item, 'sold')">
          {{ item.total_lots_sold }}
        </a>
        <span v-else>0</span>
      </template>
      <template slot="cell(total_lots_unsold)" slot-scope="{item}">
        <a v-if="item.total_lots_unsold > 0" class="mr-3" href="javascript:void(0)"
           @click="onTotalItemsClick(item, 'unsold')">
          {{ item.total_lots_unsold }}
        </a>
        <span v-else>0</span>
      </template>

      <template slot="cell(identity_verification)" slot-scope="{item}">
        {{item.customer.term ? item.customer.term.identity_verification : '' | toUpperCaseFirst }}
      </template>

      <template slot="cell(actions)" slot-scope="{item}">

        <b-dropdown right class="text-white" variant="primary" no-caret size="sm">

          <template #button-content>
            <font-awesome-icon icon="ellipsis-v" class="icon"/>
          </template>

          <b-dd-item @click="onCreateSettlementClick(item)" v-permission="'change_settlement'"
                     :disabled="item.financial_items === item.total_financial_items_released">
            <font-awesome-icon icon="plus" style="font-size: .9em" class="mr-1"/>
            {{ $t('Create Settlement') }}
          </b-dd-item>

          <b-dd-item :disabled="item.total_financial_items_released === 0"
                     @click="onSettlementsViewClick(item)" v-permission="'change_auction'">
            <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
            {{ $t('View Settlements') }}
          </b-dd-item>

          <b-dd-item :disabled="item.total_lots_unsold === 0"
                     @click="onSendProposalOfUnsoldLotsClick(item)" v-permission="'change_auction'">
            <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
            {{ $t('Send Proposal of Unsold Lots') }}
          </b-dd-item>

          <b-dd-item @click="onSendAuctionConfirmationNotification(item)" v-permission="'change_auction'">
            <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
            {{ $t('Send Auction Confirmation') }}
          </b-dd-item>

          <b-dd-item @click="onSendAuctionResultsNotification(item)" v-permission="'view_settlement'">
            <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
            {{ $t('Send Auction Results') }}
          </b-dd-item>

        </b-dropdown>


      </template>
    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";

  const {mapState, mapActions} = createNamespacedHelpers('vendors');
  const {mapActions: unsoldProposalsMapActions} = createNamespacedHelpers('vendors/unsoldProposals');
  const {mapActions: settlementsMapActions} = createNamespacedHelpers('settlements');
  const {mapActions: lotsMapActions} = createNamespacedHelpers('lots');
  const {mapActions: globalMapActions} = createNamespacedHelpers('global');

  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import _ from "lodash";
  import {SOURCE_VENDORS_TABLE, VENDORS_TABLE_NAME, WS_BATCH_SETTLEMENT_GENERATION_PROGRESS} from "@/modules/constants";
  import SettlementService from "@/modules/settlements/SettlementService";
  import VendorsSearch from "@/modules/vendors/VendorsSearch";
  import SearchCollapse from "@/core/components/search-collapse/SearchCollapse";
  import SalesService from "@/modules/sales/SalesService";
  import EventBus from "../../plugins/event-bus";
  import i18n from "../../shared/i18n";
  import {
    ON_PDF_MODAL_CLOSE,
    ON_PDF_MODAL_OPEN
  } from "../../plugins/event-bus/event-bus-names";
  import ClientTierDisplayWidget from "@/core/widgets/ClientTierDisplayWidget.vue";

  export default {
    name: "VendorsTable",
    components: {ClientTierDisplayWidget, SearchCollapse, VendorsSearch, ArtisioTable},
    props: {
      hideActions: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
    },
    data() {
      return {
        batchSettlementGenerationProgress: null,
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        loading: state => state.tableData[state.tableSource].loading,
        items: state => state.tableData[state.tableSource].rows,
        sale: state => state.tableData[state.tableSource].sale,
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource
      }),
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields).filter(field => !this.excludeColumns.includes(field.key) && field.isVisible)
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.excludeColumns,
          tableName: `${VENDORS_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions({
        getTableData: 'getTableData',
        updateToggleColumns: 'updateToggleColumns',
      }),
      ...unsoldProposalsMapActions(['showUnsoldProposalModal']),
      ...settlementsMapActions({
        updateSettlementTableSource: 'updateTableSource',
        resetSettlementTableSearchModel: 'resetSearchModel',
        updateSettlementTableSearchModel: 'updateSearchModel',
        showSettlementCreateModal: 'showModal',
      }),
      ...lotsMapActions({
        updateLotsTableSource: 'updateTableSource',
        resetLotsTableSearchModel: 'resetSearchModel',
        updateLotsTableSearchModel: 'updateSearchModel'
      }),
      ...globalMapActions(['addProgress', 'removeProgress', 'updateProgressValue']),
      onCreateSettlementClick(item) {
        this.showSettlementCreateModal({
          source: SOURCE_VENDORS_TABLE,
          customer: item.customer,
          sale: this.sale,
          disableCustomerInput: true
        })
      },
      onSettlementsViewClick(item) {
        this.updateSettlementTableSource({source: 'sale'})
        this.resetSettlementTableSearchModel()
        this.updateSettlementTableSearchModel({
          customer_uuid: item.customer.uuid,
          customer: item.customer
        })
        this.$router.push({name: "sales.settlements"})
      },
      async onSendProposalOfUnsoldLotsClick(item) {
        await this.showUnsoldProposalModal({customer: item, auction: this.sale})
      },
      async onSendAuctionConfirmationNotification(item) {
        this.$confirm(this.$i18n.t('Are you sure you want to send auction confirmation to following customer?')).then(confirmed => {
          if (!confirmed) {
            return;
          }
          SalesService.sendAuctionConfirmationNotification({
            auction_uuid: this.sale.uuid,
            customer_uuid: item.customer.uuid,
            confirmed: false
          }).then(res => {
            if (res.success && res.body && res.body.attachment) {
              EventBus.$emit(ON_PDF_MODAL_OPEN, {
                title: i18n.t(`Preview of auction confirmation PDF`),
                data: {
                  pdf_url: res.body.attachment
                },
                actions: [
                  {
                    display_name: i18n.t('Confirm And Send Notification'),
                    handler: () => {
                      SalesService.sendAuctionConfirmationNotification({
                        auction_uuid: this.sale.uuid,
                        customer_uuid: item.customer.uuid,
                        confirmed: true
                      }).then(res => {
                        if (res.success) {
                          this.$toast('success', this.$i18n.t("Auction confirmation was successfully sent"));
                        }
                        EventBus.$emit(ON_PDF_MODAL_CLOSE);
                      });
                    }
                  },
                  {
                    display_name: i18n.t('Cancel'),
                    handler: () => EventBus.$emit(ON_PDF_MODAL_CLOSE)
                  }]
              });
            } else if (res.success) {
              this.$toast('success', this.$i18n.t("Auction confirmation was successfully sent"));
            } else {
              if (res.body.non_field_errors) {
                const listItemsArr = [];
                res.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
                this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Auction Confirmation Error'));
              }
            }
          });

        });
      },
      async onSendAuctionResultsNotification(item) {
        let confirmed = await this.$confirm(this.$i18n.t('Are you sure you want to send auction results notification to following customer?'))
        if (!confirmed) {
          return;
        }
        let response = await SalesService.sendAuctionResultsNotification({
          auction_uuid: this.sale.uuid,
          customer_uuid: item.customer.uuid,
          confirmed: false
        });

        if (response.success && response.body && response.body.attachment) {
          EventBus.$emit(ON_PDF_MODAL_OPEN, {
            title: i18n.t(`Preview of auction results notification PDF`),
            data: {
              pdf_url: response.body.attachment
            },
            actions: [
              {
                display_name: i18n.t('Confirm And Send Notification'),
                handler: async () => {
                  let innerResponse = await SalesService.sendAuctionResultsNotification({
                    auction_uuid: this.sale.uuid,
                    customer_uuid: item.customer.uuid,
                    confirmed: true
                  })
                  if (innerResponse.success) {
                    this.$toast('success', this.$i18n.t("Auction results notification was successfully sent"));
                  }
                  EventBus.$emit(ON_PDF_MODAL_CLOSE);
                }
              },
              {
                display_name: i18n.t('Cancel'),
                handler: () => EventBus.$emit(ON_PDF_MODAL_CLOSE)
              }
            ]
          });
        } else if (response.success) {
          this.$toast('success', this.$i18n.t("Auction results notification was successfully sent"));
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0])
          }
        }
      },
      onTotalItemsClick(item, status = null) {
        this.updateLotsTableSource({source: 'sale'})
        this.resetLotsTableSearchModel()
        let params = {
          customer_uuid: item.customer.uuid,
          customer: item.customer,
        }
        if (status) {
          params['status'] = status
        }
        this.updateLotsTableSearchModel(params)
        this.$router.push({name: 'sales.lots'})
      },
      async onBatchSendAuctionConfirmationNotification(selectedItems) {
        let customerUuids = []
        selectedItems.forEach(i => customerUuids.push(i.customer.uuid))

        let success = await this.$confirm(this.$i18n.t('Are you sure you want to send auction confirmation notification to following vendors?'));
        if (!success) {
          return
        }

        let response = await SalesService.batchSendAuctionConfirmationNotification({
          auction_uuid: this.sale.uuid,
          customer_uuids: customerUuids
        })

        if (response.success) {
          this.$toast('success', this.$i18n.t("Auction confirmation notification was successfully initiated"));
        } else {
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Auction Confirmation Error'));
          }
        }
      },
      async onBatchSendAuctionResultsNotification(selectedItems) {
        let customerUuids = []
        selectedItems.forEach(i => customerUuids.push(i.customer.uuid))

        let success = await this.$confirm(this.$i18n.t('Are you sure you want to send auction results notification to following vendors?'));
        if (!success) {
          return
        }

        let response = await SalesService.batchSendAuctionResultsNotification({
          auction_uuid: this.sale.uuid,
          customer_uuids: customerUuids
        })

        if (response.success) {
          this.$toast('success', this.$i18n.t("Auction results notification was successfully initiated"));
        } else {
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Auction Results Notificaiton Error'));
          }
        }
      },
      async onBatchSendProposalOfUnsoldLots(selectedItems) {
        let customerUuids = []
        selectedItems.forEach(i => customerUuids.push(i.customer.uuid))

        let success = await this.$confirm(this.$i18n.t('Are you sure you want to send proposal of unsold lots notification to following vendors?'));
        if (!success) {
          return
        }

        let response = await SalesService.batchProposalOfUnsoldLotsNotification({
          auction_uuid: this.sale.uuid,
          customer_uuids: customerUuids
        })

        if (response.success) {
          this.$toast('success', this.$i18n.t("Proposal of unsold lots notification was successfully initiated"));
        } else {
          if (response.body.non_field_errors) {
            const listItemsArr = [];
            response.body.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Proposal of Unsold Lots Error'));
          }
        }
      },
      async onBatchGenerateSettlementsClick(selectedItems) {
        let customerUuids = []
        selectedItems.forEach(i => customerUuids.push(i.customer.uuid))

        let success = await this.$confirm(this.$i18n.t('Are you sure you want to generate settlements for the selected vendors?'));
        if (!success) {
          return
        }

        let {success: responseSuccess, body} = await SettlementService.batchCreateSettlements({
          auction_uuid: this.sale.uuid,
          customer_uuids: customerUuids
        })

        if (responseSuccess) {
          await this.addBatchSettlementGenerationProgressIfNotExist();
        } else {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          }
        }
      },
      async onBatchCreateNotificationReceive(data) {
        await this.addBatchSettlementGenerationProgressIfNotExist()
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.successful / data.total * 100)),
          uuid: this.batchSettlementGenerationProgress.uuid,
        });
        if (data.is_finished) {
          if (!data.errors) {
            this.$toast('success', this.$i18n.t("Batch settlement generation has successfully finished"));
          }
          this.removeProgressIfExist();
          await this.getTableData();
        }
        if (data.errors) {
          if (data.errors.non_field_errors) {
            const listItemsArr = [];
            data.errors.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t("Result"));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong while generating batch settlement. Please, try again later."));
          }
        }
      },
      async addBatchSettlementGenerationProgressIfNotExist() {
        if (!this.batchSettlementGenerationProgress) {
          this.batchSettlementGenerationProgress = await this.addProgress(
            this.$i18n.t("Batch settlement generation for current auction")
          );
        }
        return true;
      },
      removeProgressIfExist() {
        if (this.batchSettlementGenerationProgress) {
          this.removeProgress(this.batchSettlementGenerationProgress.uuid);
          this.batchSettlementGenerationProgress = null;
        }
      },
    },
    mounted() {
      this.getTableData();
      this.$ws.listen(`settlements.${WS_BATCH_SETTLEMENT_GENERATION_PROGRESS}`, this.onBatchCreateNotificationReceive);
    },
    beforeDestroy() {
      this.$ws.remove(`settlements.${WS_BATCH_SETTLEMENT_GENERATION_PROGRESS}`);
    }
  }
</script>

<style lang="scss" scoped>
  .artisio-table {
    /deep/ {
      .customer-no-col {
        min-width: 160px;
      }

      .full-name-col {
        min-width: 130px;
      }

      .company-name-col {
        min-width: 130px;
      }

      .email-col {
        min-width: 150px;

      }

      .billing-address-col {
        min-width: 250px;
      }

      .financial-items-col {
        min-width: 50px;
      }

      .total-lots-sold-col {
        min-width: 80px;
      }

      .total-lots-unsold-col {
        min-width: 100px;
      }

      .total-financial-items-released-col {
        min-width: 100px;
      }
    }
  }

</style>
